/**
 * Value transformers convert between display format and wire (API submission) format.
 * Each field type may have a specialized transformer.
 *
 * toWire: display format -> API format (for saving)
 * fromWire: API format -> display format (for loading)
 */

export interface ValueTransformer {
  toWire(value: unknown, originalType: string): unknown
  fromWire(value: unknown, originalType: string): unknown
}

class DefaultTransformer implements ValueTransformer {
  toWire(value: unknown): unknown {
    return value
  }
  fromWire(value: unknown): unknown {
    return value
  }
}

class MultiPicklistTransformer implements ValueTransformer {
  toWire(value: unknown): unknown {
    return Array.isArray(value) ? value.join(';') : value
  }
  fromWire(value: unknown): unknown {
    if (typeof value === 'string' && value) return value.split(';')
    if (Array.isArray(value)) return value
    return value
  }
}

class YesNoTransformer implements ValueTransformer {
  toWire(value: unknown, originalType: string): unknown {
    if (originalType === 'checkbox') {
      return Boolean(value)
    }
    return value
  }
  fromWire(value: unknown): unknown {
    return value
  }
}

class DateTransformer implements ValueTransformer {
  toWire(value: unknown): unknown {
    if (!value) return value

    if (typeof value === 'string') {
      const isoPattern = /^\d{4}-\d{2}-\d{2}$/
      if (isoPattern.test(value)) {
        const [yearStr, monthStr, dayStr] = value.split('-')
        const year = Number(yearStr)
        const month = Number(monthStr)
        const day = Number(dayStr)
        const d = new Date(year, month - 1, day)
        if (d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day) {
          return value
        }
      }
      const d = new Date(value)
      if (!Number.isNaN(d.getTime())) {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      }
    }

    if (value instanceof Date) {
      return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
    }

    return value
  }

  fromWire(value: unknown): unknown {
    // API sends YYYY-MM-DD, HTML date inputs expect YYYY-MM-DD, so passthrough
    return value
  }
}

class HeightTransformer implements ValueTransformer {
  toWire(value: unknown, originalType: string): unknown {
    if (!value || typeof value !== 'string') return value
    if (originalType === 'picklist' || originalType === 'number') {
      const match = value.match(/^(\d+)'(\d+)"?$/)
      if (match && match[1] && match[2]) {
        return Number.parseInt(match[1], 10) * 12 + Number.parseInt(match[2], 10)
      }
    }
    return value
  }
  fromWire(value: unknown): unknown {
    return value
  }
}

class PicklistTransformer implements ValueTransformer {
  toWire(value: unknown): unknown {
    // Empty string -> undefined (don't send blank picklist selections)
    if (value === '') return undefined
    return value
  }
  fromWire(value: unknown): unknown {
    return value
  }
}

class CheckboxTransformer implements ValueTransformer {
  toWire(value: unknown): unknown {
    if (value === '') return undefined
    return value
  }
  fromWire(value: unknown): unknown {
    return value
  }
}

class NumberTransformer implements ValueTransformer {
  toWire(value: unknown): unknown {
    if (value === '' || value === null || value === undefined) return undefined
    if (typeof value === 'string') {
      const filtered = value.replace(/[^\d.-]/g, '')
      const num = Number.parseFloat(filtered)
      return Number.isNaN(num) ? value : num
    }
    return value
  }
  fromWire(value: unknown): unknown {
    return value
  }
}

const defaultTransformer = new DefaultTransformer()

const transformers: Record<string, ValueTransformer> = {
  multipicklist: new MultiPicklistTransformer(),
  'multiselect-picklist': new MultiPicklistTransformer(),
  'multi-select': new MultiPicklistTransformer(),
  'yes-no': new YesNoTransformer(),
  date: new DateTransformer(),
  'date-time': new DateTransformer(),
  height: new HeightTransformer(),
  picklist: new PicklistTransformer(),
  checkbox: new CheckboxTransformer(),
  number: new NumberTransformer(),
  currency: new NumberTransformer(),
  percent: new NumberTransformer(),
}

export function getValueTransformer(fieldType: string): ValueTransformer {
  return transformers[fieldType] || defaultTransformer
}

/**
 * Transform all answers for API submission (display -> wire).
 */
export function transformAnswersForSubmission(
  answers: Record<string, unknown>,
  fieldTypes: Record<string, { type: string; originalType: string }>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(answers)) {
    const info = fieldTypes[key]
    if (info) {
      const transformer = getValueTransformer(info.type)
      result[key] = transformer.toWire(value, info.originalType)
    } else {
      result[key] = value
    }
  }
  return result
}

/**
 * Transform API data to display format (wire -> display).
 * Called when loading existing form data.
 */
export function transformAnswersFromWire(
  data: Record<string, unknown>,
  fieldTypes: Record<string, { type: string; originalType: string }>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(data)) {
    const info = fieldTypes[key]
    if (info) {
      const transformer = getValueTransformer(info.type)
      result[key] = transformer.fromWire(value, info.originalType)
    } else {
      result[key] = value
    }
  }
  return result
}
